import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { CaretLeft, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { PayButtons } from "@/components/PayButtons";
import { PRICING } from "@/data/categories";
import { memoryDate } from "@/components/MemoryList";
import { RefundAgree } from "@/components/RefundAgree";

const PRODUCT = PRICING.ai.find((a) => a.key === "memory_insight");

export default function MemoryInsightCheckout() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [memory, setMemory] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [refundOk, setRefundOk] = useState(false);
  const back = `/timeline?memory=${id}&insight=1`;

  useEffect(() => {
    api.get("/events").then((r) => {
      const m = r.data.find((e) => e.id === id && e.entry_type !== "dream");
      if (!m) { toast.error("Memory not found"); navigate("/timeline"); return; }
      if (m.insight_unlocked || m.memory_insight?.text) { navigate(back, { replace: true }); return; }
      setMemory(m);
    });
    api.get("/payments/config").then((r) => setCfg(r.data)).catch(() => setCfg({}));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPaid = (r) => {
    if (r.payment_status === "paid") toast.success("Your Memory Insight is unlocked.");
    else toast("You already owned this insight. This duplicate payment is being refunded.");
    navigate(back);
  };

  if (!memory || !cfg) return <p className="p-10 text-center text-[#8A7F76]">Loading…</p>;

  const body = (
    <div data-testid="memory-insight-checkout" className="mx-auto max-w-lg px-6 py-10">
      <button data-testid="insight-checkout-back" onClick={() => navigate(`/timeline?memory=${id}`)} className="mb-8 flex items-center gap-1 text-sm text-[#8A7F76] hover:text-[#A84A32]">
        <CaretLeft size={16} /> Back to my memory
      </button>
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#A84A32]"><Sparkle size={14} weight="fill" /> {PRODUCT.label}</p>
      <h1 className="mt-2 font-serif text-4xl leading-tight text-[#2C2A28]">{memory.title}</h1>
      <p className="mt-1 text-sm text-[#8A7F76]">{memoryDate(memory)} · {memory.season}</p>
      <p className="mt-5 leading-relaxed text-[#4A4540]">{PRODUCT.desc}</p>
      <div className="mt-6 rounded-xl border border-[#B08D57]/30 bg-white p-5">
        <p className="font-serif text-4xl text-[#2C2A28]" data-testid="insight-checkout-price">{PRICING.currency}{PRODUCT.price.toFixed(2)}</p>
        <p className="text-xs text-[#8A7F76]">AUD · No GST applies · one-time · for this memory only</p>
        <div className="my-4"><RefundAgree checked={refundOk} onChange={setRefundOk} testId="refund-agree-insight" /></div>
        <PayButtons payload={{ lookup_key: "memory_insight", event_id: id }} refundOk={refundOk} paypalReady={!!cfg?.client_id} paypalMode={cfg?.mode} cardEnabled={cfg?.card_enabled !== false}
          onPaid={onPaid} testPrefix="insight" />
        <p className="mt-4 text-xs leading-relaxed text-[#8A7F76]">
          After payment you'll return to this memory to give AI consent and create your insight. If writing fails, you can try again without paying again.
        </p>
      </div>
    </div>
  );

  return cfg?.client_id ? (
    <PayPalScriptProvider options={{ clientId: cfg.client_id, currency: "AUD", intent: "capture", components: "buttons", disableFunding: "card" }}>
      {body}
    </PayPalScriptProvider>
  ) : body;
}
